import React, { useContext } from "react"
import Helmet from "react-helmet"
import { useStaticQuery, graphql } from "gatsby"
import { ThemeContext } from "../../context/ThemeContext"
// Served from static/ so it stays a real URL — importing it through
// webpack inlines files under 10KB as data URIs, which produced a broken
// og:image (domain + base64 blob) on every page without a hero image.
const placeholder = "/assets/og-placeholder.webp"

type SEOProps = {
  description: string
  lang: string
  meta: Array<{ name: string; content: string }>
  title: string
  image?: string
  pathname?: string
  canonicalOverride?: string
  ogType?: string
}

export const SEO = ({
  description,
  lang,
  meta,
  title,
  image,
  pathname,
  canonicalOverride,
  ogType,
}: SEOProps) => {
  const { navOpen, filterOpen, openMenu } = useContext(ThemeContext)
  const { site } = useStaticQuery(
    graphql`
      query {
        site {
          siteMetadata {
            title
            description
            author
            domain
            siteUrl
            telephone
            address
          }
        }
      }
    `
  )

  const metaDescription = description || site.siteMetadata.description

  // Resolve a self-referencing canonical URL by joining siteUrl with the
  // page pathname. If a canonicalOverride is supplied, it wins — used for
  // duplicate-content URLs that should point at another canonical page.
  // Strip any trailing slash from the siteUrl before joining, and ensure
  // the resulting canonical always ends with a trailing slash to match
  // Gatsby's URL convention.
  const siteUrlNoSlash = (site.siteMetadata.siteUrl || "").replace(/\/$/, "")
  const targetPath = canonicalOverride || pathname || "/"
  const pathWithLeadingSlash = targetPath.startsWith("/")
    ? targetPath
    : `/${targetPath}`
  const pathWithTrailingSlash = pathWithLeadingSlash.endsWith("/")
    ? pathWithLeadingSlash
    : `${pathWithLeadingSlash}/`
  const canonicalUrl = `${siteUrlNoSlash}${pathWithTrailingSlash}`

  // Resolve the OG image to an absolute URL. Prefer a page-supplied
  // hero image; fall back to the site placeholder so social shares
  // always have *something*. Strip any trailing slash on the domain
  // before joining so we don't produce double slashes.
  const domain = (site.siteMetadata.domain || "").replace(/\/$/, "")
  const resolveImage = (img?: string): string => {
    const candidate = img || placeholder
    if (!candidate) return placeholder
    if (/^https?:\/\//i.test(candidate)) return candidate
    return `${domain}${candidate.startsWith("/") ? "" : "/"}${candidate}`
  }
  const ogImage = resolveImage(image)

  const physicianSchema = {
    "@context": "https://schema.org",
    "@type": ["MedicalOrganization", "MedicalBusiness"],
    "@id": `${site.siteMetadata.domain}#clinic`,
    name: "Dr Hawkes Clinic",
    legalName: "Harley Street Eyesthetics Ltd.",
    description:
      "Dr Hawkes Clinic offers specialist Oculoplastic Surgery, Ophthalmology and Aesthetic treatments in London, led by Consultant Surgeon Dr Elizabeth Hawkes.",
    url: site.siteMetadata.domain,
    telephone: site.siteMetadata.telephone,
    medicalSpecialty: [
      "https://schema.org/Ophthalmology",
      "Oculoplastic Surgery",
      "Aesthetic Medicine",
    ],
    address: {
      "@type": "PostalAddress",
      streetAddress: "120 Sloane St",
      addressLocality: "London",
      addressRegion: "Chelsea",
      postalCode: "SW1X 9BW",
      addressCountry: "GB",
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude: 51.4943,
      longitude: -0.1585,
    },
    employee: {
      "@type": "Physician",
      "@id": `${site.siteMetadata.domain}#physician`,
      name: "Dr Elizabeth Hawkes",
      alternateName: "Dr Elizabeth Hawkes FRCOphth MBBS BSc",
      identifier: {
        "@type": "PropertyValue",
        name: "GMC Number",
        value: "7049736",
      },
      hasCredential: [
        {
          "@type": "EducationalOccupationalCredential",
          credentialCategory: "FRCOphth",
          name: "Fellow of the Royal College of Ophthalmologists",
        },
        {
          "@type": "EducationalOccupationalCredential",
          credentialCategory: "MBBS",
          name: "Bachelor of Medicine, Bachelor of Surgery",
        },
        {
          "@type": "EducationalOccupationalCredential",
          credentialCategory: "BSc",
          name: "Bachelor of Science",
        },
      ],
      award: [
        "Tatler Top Doctor 2025",
        "Tatler Top Doctor 2026",
        "Listed among the Top 4 UK Eye Surgeons in Tatler's Beauty & Cosmetic Surgery Guide",
      ],
      knowsAbout: [
        "Oculoplastic Surgery",
        "Aesthetic Medicine",
        "Blepharoplasty",
        "Ptosis Repair",
      ],
      sameAs: [
        "https://www.instagram.com/dr_elizabethhawkes/",
        "https://www.youtube.com/@DrElizabethHawkes",
        "https://www.doctify.com/uk/specialist/ms-elizabeth-dolatshany-hawkes",
      ],
      jobTitle: "Head of Oculoplastic Surgery",
      worksFor: {
        "@type": "MedicalClinic",
        "@id": "https://www.cadoganclinic.com/#clinic",
        name: "Cadogan Clinic",
        url: "https://www.cadoganclinic.com/",
        address: {
          "@type": "PostalAddress",
          streetAddress: "120 Sloane Street",
          addressLocality: "Chelsea",
          addressRegion: "London",
          postalCode: "SW1X 9BW",
          addressCountry: "GB",
        },
      },
      // Real consulting locations for local-context signal. Both clinics
      // are independent businesses where Dr Hawkes holds consulting rights
      // (she does not own them).
      workLocation: [
        {
          "@type": "MedicalClinic",
          name: "Cadogan Clinic",
          address: {
            "@type": "PostalAddress",
            streetAddress: "120 Sloane Street",
            addressLocality: "Chelsea",
            addressRegion: "London",
            postalCode: "SW1X 9BW",
            addressCountry: "GB",
          },
          geo: {
            "@type": "GeoCoordinates",
            latitude: 51.494478,
            longitude: -0.158509,
          },
          telephone: "+447702767200",
          url: "https://www.cadoganclinic.com/",
        },
        {
          "@type": "MedicalClinic",
          name: "The London Lauriston Clinic",
          address: {
            "@type": "PostalAddress",
            streetAddress: "34 Great Titchfield Street",
            addressLocality: "London",
            postalCode: "W1W 8BQ",
            addressCountry: "GB",
          },
          geo: {
            "@type": "GeoCoordinates",
            latitude: 51.517699,
            longitude: -0.140024,
          },
          telephone: "+447702767200",
          url: "https://www.llclinics.co.uk/",
        },
      ],
    },
  }

  return (
    <Helmet
      htmlAttributes={{
        lang,
      }}
      bodyAttributes={{
        class: `${navOpen || openMenu > 0 ? "overflow-hidden" : ""} ${
          filterOpen ? "overflow-hidden" : ""
        }`,
      }}
      title={title}
      link={[{ rel: "canonical", href: canonicalUrl }]}
      script={[
        {
          type: "application/ld+json",
          // Escape ampersands to their \u0026 JSON form (identical once parsed):
          // react-helmet 5.x silently breaks Gatsby's client-side routing
          // when a raw & appears in script innerHTML — every internal link
          // then changes the URL without rendering the new page.
          innerHTML: JSON.stringify(physicianSchema).replace(/&/g, "\\u0026"),
        },
      ]}
      meta={[
        {
          name: `description`,
          content: metaDescription,
        },
        {
          property: `og:title`,
          content: title || site.siteMetadata.title,
        },
        {
          property: `og:description`,
          content: metaDescription,
        },
        {
          property: `og:type`,
          content: ogType || `website`,
        },
        {
          property: `og:url`,
          content: canonicalUrl,
        },
        {
          property: `og:image`,
          content: ogImage,
        },
        {
          property: `og:image:secure_url`,
          content: ogImage,
        },
        {
          name: `twitter:card`,
          content: `summary_large_image`,
        },
        {
          name: `twitter:image`,
          content: ogImage,
        },
        {
          name: `twitter:creator`,
          content: site.siteMetadata.author,
        },
        {
          name: `twitter:title`,
          content: title || site.siteMetadata.title,
        },
        {
          name: `twitter:description`,
          content: metaDescription,
        },
      ].concat(meta)}
    ></Helmet>
  )
}

SEO.defaultProps = {
  lang: `en`,
  meta: [],
  description: ``,
}

export default SEO
